import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Layout/Header";
import { useSelector } from "react-redux";
import socketIO from "socket.io-client";
import { format } from "timeago.js";
import { server } from "../server";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { AiOutlineArrowRight, AiOutlineSend } from "react-icons/ai";
import { TfiGallery } from "react-icons/tfi";
import { socketEndpoint } from "../config/socket";
import {
  IMAGE_ACCEPT_WITH_HEIC,
  getImagePreparationErrorMessage,
  prepareImageFileForUpload,
  readImageFileAsDataUrl,
} from "../utils/imageUploadPreparation";

const socketId = socketIO(socketEndpoint, { transports: ["websocket"] });

const UserInbox = ({ embedded = false }) => {
  const { user, loading } = useSelector((state) => state.user);
  const [conversations, setConversations] = useState([]);
  const [arrivalMessage, setArrivalMessage] = useState(null);
  const [currentChat, setCurrentChat] = useState();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [userData, setUserData] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [activeStatus, setActiveStatus] = useState(false);
  const [open, setOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const handleIncomingMessage = (data) => {
      setArrivalMessage({
        sender: data.senderId,
        text: data.text,
        createdAt: Date.now(),
      });
    };

    socketId.on("getMessage", handleIncomingMessage);

    return () => {
      socketId.off("getMessage", handleIncomingMessage);
    };
  }, []);

  useEffect(() => {
    arrivalMessage &&
      currentChat?.members.includes(arrivalMessage.sender) &&
      setMessages((prev) => [...prev, arrivalMessage]);
  }, [arrivalMessage, currentChat]);

  useEffect(() => {
    const getConversation = async () => {
      if (!user?._id) {
        return;
      }

      try {
        const response = await axios.get(
          `${server}/conversation/get-all-conversation-user/${user?._id}`,
          {
            withCredentials: true,
          }
        );
        setConversations(response.data.conversations);
      } catch (error) {
        console.error(error);
      }
    };
    getConversation();
  }, [user?._id, messages]);

  useEffect(() => {
    if (user) {
      const sellerId = user?._id;
      socketId.emit("addUser", sellerId);
      const handleOnlineUsers = (data) => {
        setOnlineUsers(data);
      };
      socketId.on("getUsers", handleOnlineUsers);

      return () => {
        socketId.off("getUsers", handleOnlineUsers);
      };
    }
  }, [user]);

  const onlineCheck = (chat) => {
    const chatMembers = chat.members.find((member) => member !== user?._id);
    const online = onlineUsers.find((user) => user.userId === chatMembers);

    return online ? true : false;
  };

  useEffect(() => {
    const getMessage = async () => {
      if (!currentChat?._id) {
        setMessages([]);
        return;
      }

      try {
        const response = await axios.get(
          `${server}/message/get-all-messages/${currentChat?._id}`
        );
        setMessages(response.data.messages);
      } catch (error) {
        console.error(error);
      }
    };
    getMessage();
  }, [currentChat]);

  const selectConversation = ({ chat, contact, online }) => {
    setCurrentChat(chat);
    setUserData(contact);
    setActiveStatus(online);
    setOpen(true);
  };

  const sendMessageHandler = async (e) => {
    e.preventDefault();

    const message = {
      sender: user._id,
      text: newMessage,
      conversationId: currentChat._id,
    };
    const receiverId = currentChat.members.find(
      (member) => member !== user?._id
    );

    socketId.emit("sendMessage", {
      senderId: user?._id,
      receiverId,
      text: newMessage,
    });

    try {
      if (newMessage !== "") {
        await axios
          .post(`${server}/message/create-new-message`, message)
          .then((res) => {
            setMessages([...messages, res.data.message]);
            updateLastMessage();
          })
          .catch((error) => {
            console.error(error);
          });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const updateLastMessage = async () => {
    socketId.emit("updateLastMessage", {
      lastMessage: newMessage,
      lastMessageId: user._id,
    });

    await axios
      .put(`${server}/conversation/update-last-message/${currentChat._id}`, {
        lastMessage: newMessage,
        lastMessageId: user._id,
      })
      .then(() => {
        setNewMessage("");
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file) {
      return;
    }

    try {
      const preparedFile = await prepareImageFileForUpload(file);
      const imageDataUrl = await readImageFileAsDataUrl(preparedFile);
      imageSendingHandler(imageDataUrl);
    } catch (error) {
      console.error("Error preparing message image:", error);
      alert(getImagePreparationErrorMessage(error));
    }
  };

  const imageSendingHandler = async (image) => {
    const receiverId = currentChat.members.find(
      (member) => member !== user._id
    );

    socketId.emit("sendMessage", {
      senderId: user._id,
      receiverId,
      images: image,
    });

    try {
      await axios
        .post(`${server}/message/create-new-message`, {
          images: image,
          sender: user._id,
          text: newMessage,
          conversationId: currentChat._id,
        })
        .then((res) => {
          setMessages([...messages, res.data.message]);
          updateLastMessageForImage();
        });
    } catch (error) {
      console.error(error);
    }
  };

  const updateLastMessageForImage = async () => {
    await axios.put(
      `${server}/conversation/update-last-message/${currentChat._id}`,
      {
        lastMessage: "Photo",
        lastMessageId: user._id,
      }
    );
  };

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const renderConversationList = () => (
    <div className="user-inbox-component-conversation-list">
      {conversations && conversations.length > 0 ? (
        conversations.map((item, index) => (
          <MessageList
            data={item}
            key={item._id || index}
            currentChatId={currentChat?._id}
            selectConversation={selectConversation}
            me={user?._id}
            online={onlineCheck(item)}
            loading={loading}
            embedded={embedded}
          />
        ))
      ) : (
        <div className="profile-settings-empty-state user-inbox-empty-state">
          <h3>No messages yet</h3>
          <p>Conversations with shops, sellers, and community contacts will appear here.</p>
          <Link to="/shops" className="profile-settings-empty-action">Find Shops</Link>
        </div>
      )}
    </div>
  );

  if (embedded) {
    return (
      <div className="user-inbox-component-container user-inbox-component-container--embedded">
        <div className="user-inbox-component-workspace">
          <aside className="user-inbox-component-contacts-panel">
            <header className="user-inbox-component-panel-header">
              <p>Inbox</p>
              <h1 className="user-inbox-component-title">Contacts & Messages</h1>
              <span>{conversations.length} conversation{conversations.length === 1 ? "" : "s"}</span>
            </header>
            {renderConversationList()}
          </aside>

          <section className="user-inbox-component-thread-panel">
            {currentChat ? (
              <SellerInbox
                setOpen={setOpen}
                newMessage={newMessage}
                setNewMessage={setNewMessage}
                sendMessageHandler={sendMessageHandler}
                messages={messages}
                sellerId={user._id}
                userData={userData}
                activeStatus={activeStatus}
                scrollRef={scrollRef}
                handleImageUpload={handleImageUpload}
                embedded={embedded}
              />
            ) : (
              <div className="user-inbox-component-thread-empty">
                <p>Messages</p>
                <h3>Select a conversation</h3>
                <span>Open a contact to review the full message history, send replies, and share images from your profile.</span>
              </div>
            )}
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className={`user-inbox-component-container ${embedded ? "user-inbox-component-container--embedded" : ""}`}>
      {!open && (
        <>
          {!embedded && <Header />}
          <h1 className="user-inbox-component-title">
            All Messages
          </h1>
          {renderConversationList()}
        </>
      )}

      {open && (
        <SellerInbox
          setOpen={setOpen}
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          sendMessageHandler={sendMessageHandler}
          messages={messages}
          sellerId={user._id}
          userData={userData}
          activeStatus={activeStatus}
          scrollRef={scrollRef}
          handleImageUpload={handleImageUpload}
          embedded={embedded}
        />
      )}
    </div>
  );
};

const MessageList = ({
  data,
  currentChatId,
  selectConversation,
  me,
  online,
  loading,
  embedded,
}) => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const handleClick = (id) => {
    if (!embedded) {
      navigate(`/inbox?${id}`);
    }
    selectConversation({ chat: data, contact: user, online });
  };

  useEffect(() => {
    const userId = data.members.find((user) => user !== me);
    const getUser = async () => {
      try {
        const res = await axios.get(`${server}/shop/get-shop-info/${userId}`);
        setUser(res.data.shop);
      } catch (error) {
        console.error(error);
      }
    };
    getUser();
  }, [me, data]);

  const contactName = user?.name || "Contact";
  const lastMessageLabel = data?.lastMessageId === me ? "You" : contactName.split(" ")[0];

  return (
    <div
      className={`user-inbox-component-message-item ${currentChatId === data?._id ? "active" : ""}`}
      onClick={() => {
        handleClick(data._id);
      }}
    >
      <div className="user-inbox-component-avatar">
        <img
          src={user?.avatar?.url || "/DefaultAvatar.png"}
          alt={contactName}
          className="user-inbox-component-avatar-img"
        />
        {online ? (
          <div className="user-inbox-component-online-indicator" />
        ) : (
          <div className="user-inbox-component-offline-indicator" />
        )}
      </div>
      <div className="user-inbox-component-message-content">
        <div className="user-inbox-component-message-meta">
          <h1 className="user-inbox-component-message-username">{contactName}</h1>
          <span>{online ? "Active" : "Offline"}</span>
        </div>
        <p className="user-inbox-component-message-text">
          {!loading && data?.lastMessage ? `${lastMessageLabel}: ${data.lastMessage}` : "Open conversation"}
        </p>
      </div>
    </div>
  );
};

const SellerInbox = ({
  setOpen,
  newMessage,
  setNewMessage,
  sendMessageHandler,
  messages,
  sellerId,
  userData,
  activeStatus,
  scrollRef,
  handleImageUpload,
  embedded,
}) => {
  return (
    <div className="user-inbox-component-chat-container">
      <div className="user-inbox-component-chat-header">
        <div className="user-inbox-component-chat-header-left">
          <img
            src={userData?.avatar?.url || "/DefaultAvatar.png"}
            alt={userData?.name || "Contact"}
            className="user-inbox-component-chat-avatar"
          />
          <div className="user-inbox-component-chat-user-info">
            <h1 className="user-inbox-component-chat-username">{userData?.name || "Contact"}</h1>
            <h1 className="user-inbox-component-chat-status">{activeStatus ? "Active Now" : "Offline"}</h1>
          </div>
        </div>
        {!embedded && (
          <AiOutlineArrowRight
            size={20}
            className="user-inbox-component-back-icon"
            onClick={() => setOpen(false)}
          />
        )}
      </div>

      <div className="user-inbox-component-messages" ref={scrollRef}>
        {messages && messages.length > 0 ? (
          messages.map((item, index) => (
            <div
              className={`user-inbox-component-message ${item.sender === sellerId ? "sent" : "received"}`}
              key={index}
            >
              {item.sender !== sellerId && (
                <img
                  src={userData?.avatar?.url || "/DefaultAvatar.png"}
                  className="user-inbox-component-message-avatar"
                  alt={userData?.name || "Contact"}
                />
              )}
              {item.images && (
                <img
                  src={`${item.images?.url}`}
                  className="user-inbox-component-message-image"
                  alt=""
                />
              )}
              {item.text !== "" && (
                <div className="user-inbox-component-message-bubble">
                  <p>{item.text}</p>
                </div>
              )}
              <p className="user-inbox-component-message-time">
                {format(item.createdAt)}
              </p>
            </div>
          ))
        ) : (
          <div className="user-inbox-component-thread-empty user-inbox-component-thread-empty--compact">
            <p>Conversation</p>
            <h3>No messages yet</h3>
            <span>Send the first message or share an image to start the thread.</span>
          </div>
        )}
      </div>

      <form className="user-inbox-component-message-input-container" onSubmit={sendMessageHandler}>
        <div className="user-inbox-component-gallery-icon">
          <input
            type="file"
            id="image"
            className="user-inbox-component-file-input"
            accept={IMAGE_ACCEPT_WITH_HEIC}
            onChange={handleImageUpload}
          />
          <label htmlFor="image">
            <TfiGallery size={20} />
          </label>
        </div>
        <div className="user-inbox-component-message-input-box">
          <input
            type="text"
            required
            placeholder="Enter your message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="user-inbox-component-message-input"
          />
          <input type="submit" value="Send" className="user-inbox-component-send-button" id="send" />
          <label htmlFor="send">
            <AiOutlineSend size={20} className="user-inbox-component-send-icon" />
          </label>
        </div>
      </form>
    </div>
  );
};

export default UserInbox;

